import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { X, Send, Bot, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Add a DevOps Engineer role at $120 onsite / $50 offshore",
  "Increase QA effort by adding a regression automation task",
  "Reduce margin to 25% — what's the new price?",
  "Raise contingency on the riskiest workstream to 30%",
];

export default function ChatPanel({ oppId, open, onClose, onEstimation }) {
  const [msgs, setMsgs] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef();

  useEffect(() => {
    if (open && msgs === null) {
      api.get(`/opportunities/${oppId}/estimation/chat`).then((r) => setMsgs(r.data)).catch(() => setMsgs([]));
    }
  }, [open]); // eslint-disable-line

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]); // eslint-disable-line react-hooks/exhaustive-deps -- ref is stable

  const send = async (text) => {
    const message = (text || input).trim();
    if (!message || busy) return;
    setInput("");
    setMsgs((m) => [...(m || []), { id: Date.now(), role: "user", content: message }]);
    setBusy(true);
    try {
      const { data } = await api.post(`/opportunities/${oppId}/estimation/chat`, { message }, { timeout: 180000 });
      setMsgs((m) => [...m, { id: Date.now() + 1, role: "assistant", content: data.reply, changes: data.changes }]);
      if (data.estimation) onEstimation(data.estimation);
    } catch (e) {
      toast.error(apiError(e));
      setMsgs((m) => [...m, { id: Date.now() + 1, role: "assistant", content: "Sorry — that request failed. Please try again.", changes: [] }]);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex justify-end">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} data-testid="chat-overlay" />
      <div data-testid="chat-panel" className="relative w-full max-w-md bg-white border-l border-zinc-200 flex flex-col h-full shadow-xl">
        <div className="flex items-center gap-2 px-4 h-14 border-b border-zinc-200 shrink-0">
          <div className="w-7 h-7 bg-[#002FA7] rounded-sm flex items-center justify-center">
            <Bot size={15} className="text-white" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-sm font-medium leading-none">Estimation assistant</p>
            <p className="text-[10px] text-zinc-400 mt-0.5">Changes apply live &amp; are audit-logged</p>
          </div>
          <button data-testid="chat-close-button" onClick={onClose} className="ml-auto text-zinc-400 hover:text-zinc-900 p-1">
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {msgs === null ? (
            <p className="text-xs text-zinc-400">Loading…</p>
          ) : msgs.length === 0 && (
            <div className="space-y-2">
              <p className="text-xs text-zinc-500 mb-3">Ask me to fine-tune the estimation — add resources or skills, adjust effort, contingency, margins, timeline…</p>
              {SUGGESTIONS.map((s) => (
                <button key={s} data-testid="chat-suggestion" onClick={() => send(s)}
                  className="block w-full text-left text-xs border border-zinc-200 hover:border-[#002FA7] hover:text-[#002FA7] rounded-sm px-3 py-2 transition-colors">
                  {s}
                </button>
              ))}
            </div>
          )}
          {(msgs || []).map((m) => (
            <div key={m.id} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
              <div className={`max-w-[85%] rounded-sm px-3 py-2 text-[13px] leading-relaxed whitespace-pre-wrap ${
                m.role === "user" ? "bg-[#002FA7] text-white" : "bg-zinc-100 text-zinc-900"}`}>
                {m.content}
                {m.changes?.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-zinc-200 space-y-1">
                    {m.changes.map((c, i) => (
                      <p key={`${m.id}-chg-${i}`} className="flex items-start gap-1.5 text-[11px] text-emerald-700">
                        <Check size={11} className="mt-0.5 shrink-0" /> {c}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex items-center gap-2 text-xs text-zinc-400">
              <Loader2 size={13} className="animate-spin" /> Thinking…
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="border-t border-zinc-200 p-3 pb-14 shrink-0">
          <div className="flex gap-2">
            <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="e.g. Add a Security Engineer and a pen-test task…"
              className="flex-1 border border-zinc-300 rounded-sm px-3 py-2 text-sm outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]" />
            <button data-testid="chat-send-button" onClick={() => send()} disabled={busy || !input.trim()}
              className="bg-[#002FA7] hover:bg-[#002280] text-white px-3 rounded-sm disabled:opacity-40">
              <Send size={15} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
